import { appConfig, type AppConfig } from "../config";
import {
  getConsentState,
  subscribeToConsent,
  type ConsentState,
} from "../privacy/consent";
import type { AnalyticsEventName, PublicAnalyticsMetadata } from "./api";

const META_SCRIPT_ID = "conan-meta-pixel";
const META_SCRIPT_URL = "https://connect.facebook.net/en_US/fbevents.js";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const META_EVENT_MAP: Partial<Record<AnalyticsEventName, string>> = {
  FunnelPageView: "PageView",
  QuizStart: "QuizStart",
  Quiz25: "QuizProgress",
  Quiz50: "QuizProgress",
  Quiz75: "QuizProgress",
  QuizComplete: "QuizComplete",
  Lead: "Lead",
  ResultView: "ResultView",
  OfferView: "OfferView",
  OfferClick: "OfferClick",
  MonthlyPlanClick: "MonthlyPlanClick",
  AnnualPlanClick: "AnnualPlanClick",
  InitiateCheckout: "InitiateCheckout",
  Purchase: "Purchase",
};

const STANDARD_META_EVENTS = new Set(["PageView", "Lead", "InitiateCheckout", "Purchase"]);
const FORBIDDEN_KEY = /(answer|restriction|diet|food|health|goal|score|diagnos|profile|consideration|email|phone|whatsapp|name)/i;

type MetaCommand = [string, ...unknown[]];

interface MetaPixelFunction {
  (...args: MetaCommand): void;
  callMethod?: (...args: MetaCommand) => void;
  queue: MetaCommand[];
  loaded: boolean;
  version: string;
}

interface MetaWindow extends Window {
  fbq?: MetaPixelFunction;
  _fbq?: MetaPixelFunction;
}

export type MetaDeliveryStatus =
  | "dispatched"
  | "skipped"
  | "blocked-consent"
  | "blocked-sensitive-context"
  | "unconfigured"
  | "unsupported-event"
  | "unavailable";

export interface MetaTrackResult {
  provider: "meta";
  status: MetaDeliveryStatus;
  eventId: string;
  eventName: AnalyticsEventName;
  providerEventName?: string;
}

export interface MetaTrackOptions {
  eventId: string;
  metadata?: PublicAnalyticsMetadata;
  /** Sanitized route supplied by the first-party analytics envelope. */
  pagePath?: string;
  consent?: ConsentState;
}

export interface MetaPixelAdapterOptions {
  config?: AppConfig;
  window?: Window;
  document?: Document;
  loadTimeoutMs?: number;
}

function progressForEvent(eventName: AnalyticsEventName): number | undefined {
  if (eventName === "Quiz25") return 25;
  if (eventName === "Quiz50") return 50;
  if (eventName === "Quiz75") return 75;
  return undefined;
}

export function sanitizeAdvertisingMetadata(
  eventName: AnalyticsEventName,
  metadata: PublicAnalyticsMetadata | undefined,
): Record<string, string | number> {
  const output: Record<string, string | number> = {};
  const progress = progressForEvent(eventName);
  if (progress) output.progress = progress;
  if (!metadata) return output;

  for (const [key, value] of Object.entries(metadata)) {
    if (FORBIDDEN_KEY.test(key)) continue;
    if (key === "currency" && typeof value === "string" && /^[A-Za-z]{3}$/.test(value)) {
      output.currency = value.toUpperCase();
    }
    if (key === "value" && typeof value === "number" && Number.isFinite(value) && value >= 0) {
      output.value = value;
    }
    if (key === "valueMinor" && typeof value === "number" && Number.isInteger(value) && value >= 0) {
      output.value = value / 100;
    }
    if (key === "variant" && typeof value === "string" && /^(?:A|B)$/.test(value)) {
      output.variant = value;
    }
  }
  return output;
}

function createFbq(browser: MetaWindow): MetaPixelFunction {
  const existing = browser.fbq;
  if (existing) return existing;
  const fbq = function (...args: MetaCommand) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as MetaPixelFunction;
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  browser.fbq = fbq;
  browser._fbq = fbq;
  return fbq;
}

export class MetaPixelAdapter {
  private readonly config: AppConfig;
  private readonly browser: MetaWindow | undefined;
  private readonly page: Document | undefined;
  private readonly loadTimeoutMs: number;
  private loadPromise: Promise<boolean> | undefined;
  private initializedPixelId: string | undefined;
  private consentRevision = 0;
  private observedConsentAllowed: boolean | undefined;

  constructor(options: MetaPixelAdapterOptions = {}) {
    this.config = options.config ?? appConfig;
    this.browser = options.window ?? (typeof window === "undefined" ? undefined : window);
    this.page = options.document ?? (typeof document === "undefined" ? undefined : document);
    this.loadTimeoutMs = options.loadTimeoutMs ?? 5_000;
  }

  applyConsent(state?: ConsentState): void {
    this.observedConsentAllowed = this.isAllowed(state);
    this.consentRevision += 1;
    const fbq = this.browser?.fbq;
    if (!fbq) return;
    fbq("consent", this.observedConsentAllowed ? "grant" : "revoke");
  }

  async track(eventName: AnalyticsEventName, options: MetaTrackOptions): Promise<MetaTrackResult> {
    if (!UUID_PATTERN.test(options.eventId)) throw new TypeError("Meta eventId must be an RFC 4122 UUID v4");
    const providerEventName = META_EVENT_MAP[eventName];
    if (!providerEventName) {
      return { provider: "meta", status: "unsupported-event", eventId: options.eventId, eventName };
    }
    if (!this.config.metaPixelId) {
      return { provider: "meta", status: "unconfigured", eventId: options.eventId, eventName, providerEventName };
    }
    const sensitiveContext = this.hasSensitiveLocation();
    const publicPagePath = this.publicPagePath(options.pagePath);
    if (sensitiveContext && !publicPagePath) {
      return { provider: "meta", status: "blocked-sensitive-context", eventId: options.eventId, eventName, providerEventName };
    }
    const overridePagePath = publicPagePath && publicPagePath !== this.browser?.location.pathname
      ? publicPagePath
      : undefined;
    if (!this.isAllowed(options.consent)) {
      return { provider: "meta", status: "blocked-consent", eventId: options.eventId, eventName, providerEventName };
    }
    const consentRevision = this.consentRevision;
    if (!await this.load(overridePagePath)) {
      return { provider: "meta", status: "unavailable", eventId: options.eventId, eventName, providerEventName };
    }
    if (this.consentRevision !== consentRevision && this.observedConsentAllowed === false) {
      return { provider: "meta", status: "blocked-consent", eventId: options.eventId, eventName, providerEventName };
    }
    const fbq = this.browser?.fbq;
    if (!fbq) {
      return { provider: "meta", status: "unavailable", eventId: options.eventId, eventName, providerEventName };
    }
    // Never leave a queued Meta command on a token-bearing route: a delayed
    // queue consumer could resolve document.location after the safe URL has
    // already been restored.
    if ((sensitiveContext || overridePagePath) && !fbq.callMethod) {
      return { provider: "meta", status: "blocked-sensitive-context", eventId: options.eventId, eventName, providerEventName };
    }
    const metadata = sanitizeAdvertisingMetadata(eventName, options.metadata);
    const command = STANDARD_META_EVENTS.has(providerEventName) ? "track" : "trackCustom";
    const dispatch = () => fbq(command, providerEventName, metadata, { eventID: options.eventId });
    if (overridePagePath) {
      if (!this.runWithPublicLocation(overridePagePath, dispatch)) {
        return { provider: "meta", status: "blocked-sensitive-context", eventId: options.eventId, eventName, providerEventName };
      }
    } else {
      dispatch();
    }
    if (this.config.trackingDebug) {
      console.debug("[tracking:meta]", { eventName: providerEventName, eventId: options.eventId, keys: Object.keys(metadata) });
    }
    return { provider: "meta", status: "dispatched", eventId: options.eventId, eventName, providerEventName };
  }

  private isAllowed(state?: ConsentState): boolean {
    const decision = state ?? getConsentState();
    return !this.config.marketingConsentRequired || decision.marketing;
  }

  private hasSensitiveLocation(): boolean {
    const path = this.browser?.location.pathname ?? "";
    return /^\/b\/quiz\/resultado\/[^/]+/.test(path)
      || /^\/quiz\/resultado\/[^/]+/.test(path)
      || /^\/oferta\/[^/]+/.test(path);
  }

  private publicPagePath(pagePath: string | undefined): string | undefined {
    if (!pagePath) return undefined;
    const mappings: Record<string, string> = {
      "/b/quiz/resultado/:token": "/b/quiz/resultado",
      "/quiz/resultado/:token": "/quiz/resultado",
      "/oferta/:token": "/oferta",
    };
    const mapped = mappings[pagePath];
    if (mapped) return mapped;
    if (pagePath.length > 180 || !/^\/(?:[A-Za-z0-9._~-]+\/?)*$/.test(pagePath)) return undefined;
    return pagePath;
  }

  private runWithPublicLocation(pagePath: string, callback: () => void): boolean {
    if (!this.browser) return false;
    const previousUrl = `${this.browser.location.pathname}${this.browser.location.search}${this.browser.location.hash}`;
    const previousState: unknown = this.browser.history.state;
    try {
      this.browser.history.replaceState(previousState, "", pagePath);
      callback();
      return true;
    } catch {
      return false;
    } finally {
      try {
        this.browser.history.replaceState(previousState, "", previousUrl);
      } catch {
        // Same-origin replaceState should be available in supported browsers.
      }
    }
  }

  private async load(publicPagePath?: string): Promise<boolean> {
    const pixelId = this.config.metaPixelId;
    if (!pixelId || !this.browser || !this.page) return false;
    if (this.initializedPixelId === pixelId && this.browser.fbq) return true;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise<boolean>((resolve) => {
      const fbq = createFbq(this.browser!);
      const initialize = () => {
        fbq("consent", "grant");
        fbq("init", pixelId);
        fbq("set", "autoConfig", false, pixelId);
      };
      if (publicPagePath) {
        if (!this.runWithPublicLocation(publicPagePath, initialize)) {
          resolve(false);
          return;
        }
      } else {
        initialize();
      }
      this.initializedPixelId = pixelId;

      const existing = this.page!.getElementById(META_SCRIPT_ID) as HTMLScriptElement | null;
      if (existing?.dataset.loaded === "true") {
        resolve(true);
        return;
      }
      const script = existing ?? this.page!.createElement("script");
      const timeout = globalThis.setTimeout(() => resolve(false), this.loadTimeoutMs);
      const finish = (loaded: boolean) => {
        globalThis.clearTimeout(timeout);
        if (loaded) script.dataset.loaded = "true";
        resolve(loaded);
      };
      script.addEventListener("load", () => finish(true), { once: true });
      script.addEventListener("error", () => finish(false), { once: true });
      if (!existing) {
        script.id = META_SCRIPT_ID;
        script.async = true;
        script.src = META_SCRIPT_URL;
        this.page!.head.appendChild(script);
      }
    });
    const loaded = await this.loadPromise;
    if (!loaded) {
      this.loadPromise = undefined;
      this.initializedPixelId = undefined;
      this.page?.getElementById(META_SCRIPT_ID)?.remove();
    }
    return loaded;
  }
}

export const metaPixel = new MetaPixelAdapter();

subscribeToConsent((state) => metaPixel.applyConsent(state));

export function trackMetaEvent(eventName: AnalyticsEventName, options: MetaTrackOptions): Promise<MetaTrackResult> {
  return metaPixel.track(eventName, options);
}
